<script setup lang="ts">
import type {
  IconConstant,
} from '@tpg/grammar-web-icons';
import {
  tpgIconArrowDropDown,
  tpgIconArrowDropUp,
  tpgIconDragIndicator,
  tpgIconLayers,
  tpgIconMoreVert,
  tpgIconNoVisibility,
  tpgIconPadlock,
  tpgIconRadioButtonChecked,
  tpgIconRadioButtonUnchecked,
  tpgIconVisibility,
} from '@tpg/grammar-web-icons';
import { computed, toRefs } from 'vue';

import type { GrammarMenuItemProps } from '@nora-monorepo/grammar-ui';
import {
  GrammarBadge,
  GrammarDivider,
  GrammarIcon,
  GrammarListItem,
  GrammarMenuItem,
  GrammarQuaternaryButton,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import type { DrawLayerResponseDTO, SymbolDTO } from '@nora-monorepo/nora-api-client';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';

import Symbol from '#nora-ui/map/components/drawtools/symbol-tool/Symbol.vue';

interface MenuItemRowProps {
  title: string;
  selected?: boolean;
  visible: boolean;
  locked?: boolean;
  selectable?: boolean;
  draggable?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  menuItems?: GrammarMenuItemProps[];
  isRadioButton?: boolean;
  icon?: IconConstant;
  drawLayer?: DrawLayerResponseDTO;
  customSymbol?: SymbolDTO | null;
}

interface MenuItemRowEmits {
  tpgSelect: [selected: boolean];
  visibilityChanged: [visible: boolean];
  expand: [expanded: boolean];
  dragStart: [event: DragEvent];
  dragEnd: [];
}

const props = withDefaults(defineProps<MenuItemRowProps>(), {
  selectable: false,
});
const emits = defineEmits<MenuItemRowEmits>();

const {
  title,
  selected,
  visible,
  locked,
  selectable,
  draggable,
  menuItems,
  expanded,
  isRadioButton,
  expandable,
  icon,
  drawLayer,
  customSymbol,
} = toRefs(props);

function onSelectItem() {
  if (!selectable.value) {
    return;
  }

  emits('tpgSelect', !selected.value);
}

function onExpand() {
  emits('expand', !expanded.value);
}

function onVisibilityChanged() {
  emits('visibilityChanged', !visible.value);
}

function onDragStart(event: DragEvent): void {
  emits('dragStart', event);
}

function onDragEnd(): void {
  emits('dragEnd');
}

const menuDisabled = computed(() => {
  return !menuItems.value || menuItems.value.length === 0;
});

const defaultLayerIcon = computed(() => {
  return icon.value || tpgIconLayers;
});

const shouldShowDefaultLayerIcon = computed(() => {
  if (expandable.value || customSymbol.value) {
    return false;
  }

  return drawLayer.value?.type !== DrawLayerTypeEnum.GEOFENCE;
});

const visibilityIcon = computed(() => {
  if (isRadioButton.value) {
    return visible.value
      ? tpgIconRadioButtonChecked
      : tpgIconRadioButtonUnchecked;
  }
  return visible.value ? tpgIconVisibility : tpgIconNoVisibility;
});
</script>

<template>
  <GrammarListItem
    :current-indicator="selected"
    :class="{ 'cursor-pointer': selectable }"
    @click="onSelectItem"
  >
    <template #header-leading-buttons>
      <div class="flex min-w-0 flex-1 items-center">
        <span @tpg-click.stop>
          <GrammarQuaternaryButton
            v-if="expandable"
            :icon-template="expanded ? tpgIconArrowDropUp : tpgIconArrowDropDown"
            @click="onExpand"
          />
        </span>
        <button
          v-if="draggable"
          type="button"
          draggable="true"
          aria-label="Reorder"
          class="flex items-center border-0 bg-transparent p-0 pr-1 cursor-grab active:cursor-grabbing"
          @tpg-click.stop
          @dragstart="onDragStart"
          @dragend="onDragEnd"
        >
          <GrammarIcon
            size="large"
            class="pl-1"
            :icon-template="tpgIconDragIndicator"
            style="--iconVectorFill: var(--colorNeutralForegroundDefault)"
          />
        </button>
        <button
          type="button"
          :aria-label="title"
          class="flex min-w-0 flex-1 items-center border-0 bg-transparent p-0 text-left pointer-events-none"
        >
          <Symbol
            v-if="!expandable && customSymbol"
            class="pr-2 pl-1 px-1"
            :symbol="customSymbol"
          />

          <GrammarIcon
            v-else-if="shouldShowDefaultLayerIcon"
            class="px-2"
            size="large"
            :icon-template="defaultLayerIcon"
            style="--iconVectorFill: var(--colorNeutralForegroundDefault)"
          />

          <span class="truncate font-semibold text-light-label-primary dark:text-dark-label-primary">
            {{ title }}
          </span>
        </button>
      </div>
    </template>

    <template #header-trailing-buttons>
      <span class="flex items-center">
        <GrammarBadge
          v-if="drawLayer?.isDefault"
          class="pr-2"
          label="Default"
          title="Default draw layer"
        />

        <GrammarIcon
          v-if="locked"
          class="pr-5 pt-3"
          :icon-template="tpgIconPadlock"
        />

        <GrammarDivider
          class="py-1 box-border"
          orientation="vertical"
        />
        <span @tpg-click.stop>
          <GrammarQuaternaryButton
            :icon-template="visibilityIcon"
            @click="onVisibilityChanged"
          />
        </span>
        <GrammarDivider
          class="py-1 box-border"
          orientation="vertical"
        />
        <span @tpg-click.stop>
          <GrammarQuaternaryMenuButton
            :disabled="menuDisabled"
            :icon-template="tpgIconMoreVert"
          >
            <GrammarMenuItem
              v-for="(item, key) in menuItems"
              :key="key"
              v-bind="item"
            />
          </GrammarQuaternaryMenuButton>
        </span>
      </span>
    </template>
  </GrammarListItem>
</template>
